import React from 'react';

import {TFunction} from 'i18next';
import {generatePath} from 'react-router-dom';

import {Link} from 'components/ui-kit/Link';
import {StatusWrapper} from 'components/ui-kit/PageComponents';
import {ProductsPackageType, ProductStatus} from 'core/dictionaries';
import {ROUTE} from 'core/routes';
import {breakpoint} from 'theme';
import {
    GridFieldConfig,
    ProductContentAccessors,
    ProductContentFormattedRow,
    ProductContentRawRow,
} from 'types';
import {useMatchMedia} from 'utils/custom-hooks';
import {formatDate} from 'utils/date';
import {handleNil} from 'utils/handleNil';
import {toHex} from 'utils/string';

import {PRODUCT_STATUS_COLORS} from '../../../../core/constants/statuses';

import {AggregateContentList} from './List';
import {AggregateContentTable} from './Table';

const getGridColumns = (t: TFunction): GridFieldConfig[] => [
    {
        id: ProductContentAccessors.name,
        header: t('pages.products.grid.product'),
        isSortable: false,
        width: 200,
    },
    {
        id: ProductContentAccessors.code,
        header: t('pages.products.grid.code'),
        isSortable: false,
        width: 260,
    },
    {
        id: ProductContentAccessors.modifiedDate,
        header: t('pages.products.grid.changeDate'),
        isSortable: false,
        width: 200,
    },
    {
        id: ProductContentAccessors.packageType,
        header: t('pages.products.grid.package'),
        isSortable: false,
        width: 170,
    },
    {
        id: ProductContentAccessors.manufacturer,
        header: t('pages.products.grid.manufacturer'),
        isSortable: false,
        width: 200,
    },
    {
        id: ProductContentAccessors.status,
        header: t('pages.products.grid.status'),
        isSortable: false,
        width: 180,
    },
];

export function convertData(
    data: ProductContentRawRow[] | undefined,
    t: TFunction,
): ProductContentFormattedRow[] {
    if (!data) {
        return [];
    }

    return data.map((elem) => ({
        [ProductContentAccessors.name]: handleNil(elem.productName),
        [ProductContentAccessors.code]: handleNil(elem.markingCode, (value: string) => (
            <Link
                to={generatePath(ROUTE.PRODUCTS_DETAIL, {
                    id: toHex(`${elem.gtin}§${value}`),
                })}
                style={{wordBreak: 'break-all'}}
            >
                {value}
            </Link>
        )),
        [ProductContentAccessors.modifiedDate]: handleNil(elem.modifiedAt, (value: string) =>
            formatDate(value, 'dd.MM.yyyy'),
        ),
        [ProductContentAccessors.packageType]: handleNil(
            elem.packagingType,
            (value: ProductsPackageType) => t(`dictionaries.products.packageType.${value}`),
        ),
        [ProductContentAccessors.manufacturer]: handleNil(elem.manufacturerName),
        [ProductContentAccessors.status]: handleNil(elem.status, (value: ProductStatus) => (
            <StatusWrapper color={PRODUCT_STATUS_COLORS[value ?? 'DEFAULT']}>
                {t(`dictionaries.products.status.${value}`)}
            </StatusWrapper>
        )),
    }));
}

interface Props {
    markingCode: string;
    gtin: string | null;
}

export const AggregateContent = ({markingCode, gtin}: Props) => {
    const isMobile = useMatchMedia(`(max-width: ${breakpoint.phone})`);

    return (
        <>
            {!isMobile && (
                <AggregateContentTable
                    markingCode={markingCode}
                    gtin={gtin}
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                />
            )}
            {isMobile && (
                <AggregateContentList
                    markingCode={markingCode}
                    gtin={gtin}
                    configFunc={getGridColumns}
                    dataMapper={convertData}
                />
            )}
        </>
    );
};
